import { AccountInfo } from '@azure/msal-browser';
import { createContext, useContext } from 'react';
import { ExtendedAccountInfo, UserCapabilities } from '../types/types';

// Create User context
export const UserContext = createContext<ExtendedAccountInfo | null>(null);

// Create custom hook for using User context
export function useUser(): ExtendedAccountInfo | null {
    return useContext(UserContext);
}

/**
 * Hook to get user capabilities from the user profile.
 * Returns null if user profile hasn't been loaded yet.
 */
export function useUserCapabilities(): UserCapabilities | null {
    const user = useContext(UserContext);
    return user?.userProfile?.capabilities || null;
}

/**
 * Hook to check if the current user has debug access.
 * Returns false if user profile hasn't been loaded or user doesn't have access.
 *
 * @returns {boolean} True if user has debug access, false otherwise
 */
export function useHasDebugAccess(): boolean {
    const capabilities = useUserCapabilities();
    return capabilities?.hasDebugAccess ?? false;
}

/**
 * Hook to check if the current user has financial data access.
 * Returns false if user profile hasn't been loaded or user doesn't have access.
 *
 * @returns {boolean} True if user has financial data access, false otherwise
 */
export function useHasFinancialAccess(): boolean {
    const capabilities = useUserCapabilities();
    return capabilities?.hasFinancialDataAccess ?? false;
}

/**
 * Hook to check if the current user has export access.
 * Returns false if user profile hasn't been loaded or user doesn't have access.
 *
 * @returns {boolean} True if user has export access, false otherwise
 */
export function useHasExportAccess(): boolean {
    const capabilities = useUserCapabilities();
    return capabilities?.hasExportAccess ?? false;
}

/**
 * Hook to check if the current user has admin access.
 * Returns false if user profile hasn't been loaded or user doesn't have access.
 *
 * @returns {boolean} True if user has admin access, false otherwise
 */
export function useHasAdminAccess(): boolean {
    const capabilities = useUserCapabilities();
    return capabilities?.hasAdminAccess ?? false;
}

/**
 * Hook to check if the current user has Coretext team access.
 * Returns false if user profile hasn't been loaded or user doesn't have access.
 *
 * @returns {boolean} True if user has Coretext team access, false otherwise
 */
export function useHasCoretextTeamAccess(): boolean {
    const capabilities = useUserCapabilities();
    return capabilities?.hasCoretextTeamAccess ?? false;
}

/**
 * Hook to check if the current user has general access.
 * Returns false if user profile hasn't been loaded or user doesn't have access.
 *
 * @returns {boolean} True if user has general access, false otherwise
 */
export function useHasGeneralAccess(): boolean {
    const capabilities = useUserCapabilities();
    return capabilities?.hasGeneralAccess ?? false;
}
